import type { Job } from "../types";
import { compareSampleIds, parseSampleChanges, type SampleChange } from "./sampleChanges";

// The "Skipped Samples" email ops sends a customer when a job comes back with samples unpulled:
// which job, how many samples were wanted and how many skipped, and the exit interview's line for
// each changed sample. This module supplies the composer's prefills — the body text
// (buildSkipsEmail), the subject (buildSkipsEmailSubject), and the To-field parsing
// (parseEmailList). The map is rendered and embedded by the API; nothing here touches it.

/** The subset of Job the email reads. All-optional so a partial fixture can be passed. */
export type SkipsEmailFields = Partial<
	Pick<
		Job,
		| "name"
		| "growerName"
		| "farmName"
		| "fieldName"
		| "submitterName"
		| "numberOfSamples"
		| "exeSamplesSkipped"
		| "sampleChanges"
	>
>;

/** Numeric sample ids in order, then the rest as the log arrives (unsorted). */
const bySampleId = (a: SampleChange, b: SampleChange): number => compareSampleIds(a.sampleId, b.sampleId);

/** `- Sample 3 - Skip - Too Wet`; a change without a reason ends at its type. */
const changeLine = ({ sampleId, changeType, reason }: SampleChange): string =>
	`- ${[`Sample ${sampleId}`, changeType, reason].filter(Boolean).join(" - ")}`;

/** Grower, farm and field — the leading segments of the Airtable job name — joined the way that
 *  formula does. Used both as the body's stand-in for an empty name and as the subject line. */
function growerFarmField(job: Pick<SkipsEmailFields, "growerName" | "farmName" | "fieldName">): string {
	return [job.growerName, job.farmName, job.fieldName].filter(Boolean).join("__");
}

/** The email's body as one block of text. What happened and what to do about it is the
 *  operator's to write once it is pasted; nothing here is theirs. */
export function buildSkipsEmail(job: SkipsEmailFields): string {
	const jobName = job.name || growerFarmField(job);
	const { changes, unparsed } = parseSampleChanges(job.sampleChanges);
	const reasons = [...changes].sort(bySampleId).map(changeLine);

	const paragraphs = [
		// Addressed to whoever submitted the job. With no submitter on record there is no greeting,
		// and the operator writes their own, rather than a line with nobody's name in it.
		job.submitterName?.trim() ? `${job.submitterName.trim()},` : "",
		`${jobName ? `Job ${jobName}` : "This job"} has been submitted with skips. Please review for potential rescheduling.`,
		job.numberOfSamples === undefined ? "" : `Desired Samples: ${job.numberOfSamples}`,
		job.exeSamplesSkipped === undefined ? "" : `Skipped Samples: ${job.exeSamplesSkipped}`,
		// A line the log's shape does not cover still belongs in the email, verbatim.
		reasons.length + unparsed.length === 0 ? "" : ["Change Reasons", ...reasons, ...unparsed].join("\n"),
	];
	return paragraphs.filter(Boolean).join("\n\n");
}

const SUBJECT_SUFFIX = "Skipped Samples";

/** The composer's prefilled subject: `{growerName}__{farmName}__{fieldName} - Skipped Samples`,
 *  with any missing part dropped rather than leaving a stray "__". */
export function buildSkipsEmailSubject(job: Pick<SkipsEmailFields, "growerName" | "farmName" | "fieldName">): string {
	return [growerFarmField(job), SUBJECT_SUFFIX].filter(Boolean).join(" - ");
}

/** Splits the composer's To field into addresses: comma/semicolon/whitespace separated, each
 *  trimmed, repeats dropped case-insensitively (the first spelling wins). Does not validate
 *  shape — that's `isValidEmail`, applied by the caller so it can report which entries are bad. */
export const parseEmailList = (text: string): string[] => {
	const seen = new Set<string>();
	const addresses: string[] = [];
	for (const raw of text.split(/[,;\s]+/)) {
		const address = raw.trim();
		if (!address) continue;
		const key = address.toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		addresses.push(address);
	}
	return addresses;
};
